import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useUpdateCategoryDefaultBudgetMutation } from '../../features/budgets/queries';
import pt from '../../locales/pt';
import { extractMutationError } from '../../utils/apiError';
import { parsePtNumber } from '../../utils/money';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { NumericInput } from '../common/NumericInput';

const schema = z.object({
  amount: z.string().min(1, 'Indica um valor.'),
});

function resolveCategoryId(category) {
  return category?.category_id || category?.id || null;
}

export function AdjustCategoryBudgetDialog({
  isOpen,
  onClose,
  category,
  currentBudget,
}) {
  const { handleSubmit, control, reset, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount: '' },
  });

  const updateBudget = useUpdateCategoryDefaultBudgetMutation();

  useEffect(() => {
    if (!isOpen) return;
    reset({
      amount:
        currentBudget !== null && currentBudget !== undefined
          ? String(currentBudget).replace('.', ',')
          : '',
    });
  }, [isOpen, currentBudget, reset]);

  if (!category) return null;

  const categoryId = resolveCategoryId(category);
  const categoryLabel = category.category_name || category.name || '';

  const submit = (values) => {
    const numeric = parsePtNumber(values.amount);
    if (numeric === null || numeric < 0) {
      toast.error(pt.budget.adjust.amountLabel);
      return;
    }
    updateBudget.mutate(
      {
        categoryId,
        amount: numeric.toFixed(2),
      },
      {
        onSuccess: () => {
          toast.success(pt.budget.adjust.saved);
          onClose();
        },
        onError: (err) => {
          toast.error(extractMutationError(err, pt.budget.adjust.saveError));
        },
      },
    );
  };

  const dialogTitle = categoryLabel
    ? pt.budget.adjust.titleFor.replace('{category}', categoryLabel)
    : pt.budget.adjust.titleFallback;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-space-4" onSubmit={handleSubmit(submit)}>
          <div className="flex flex-col gap-space-1">
            <label className="type-label font-semibold text-muted-foreground">
              {pt.budget.adjust.amountLabel}
            </label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <NumericInput
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              )}
            />
            {formState.errors.amount ? (
              <p className="type-body-sm font-normal text-error">{formState.errors.amount.message}</p>
            ) : null}
          </div>

          <div className="flex justify-end gap-space-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {pt.common.cancel}
            </Button>
            <Button type="submit" disabled={updateBudget.isPending}>
              {pt.budget.adjust.save}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
